import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject, timer } from "rxjs";
import { VersionService } from './features/about/children/version/version.service';
import { AppSettingsService } from '@shared/services/app-settings/app-settings.service';
import { SignalsService } from '@shared/services/signals/signals.service';
import { TextSizeService } from '@shared/services/text-size.service';
import { AdminService } from './features/admin/admin.service';
import { ApiCacheService } from '@shared/services/cache-service/api-cache.service';

interface CustomWindow extends Window {
  userTheme?: string;
  openedFromApaPilot?: boolean;
}

declare let window: CustomWindow;

@Injectable({
  providedIn: 'root'
})
export class AppService {

  openedFromApaPilot = signal<boolean>(false);
  userTheme$ = signal<boolean>(false);

  private _theme = new BehaviorSubject<'light' | 'dark'>('light');
  theme$ = this._theme.asObservable();

  private readonly localStorageKeys = {
    theme: 'app.theme',
  };

  constructor(@Inject(DOCUMENT)
    private document: Document,
    private versionService: VersionService,
    private appSettings: AppSettingsService,
    private signalService: SignalsService,
    private textSizeService: TextSizeService,
    private adminService: AdminService,
    private cacheService: ApiCacheService,
  ) {

    // Load saved settings from local storage.
    if (localStorage.getItem(this.localStorageKeys.theme) !== null) {
      this.setTheme(
        JSON.parse(localStorage.getItem(this.localStorageKeys.theme) || '')
      );
    }
  }

  initMobileAppWatcher(): void {
    timer(0, 500).subscribe(() => {
      if (window?.userTheme) {
        if (window.userTheme === 'light' && this._theme.getValue() === 'dark') {
          this.setTheme('light');
        } else if (
          window.userTheme === 'dark' &&
          this._theme.getValue() === 'light'
        ) {
          this.setTheme('dark');
        }
      }

      if (window?.openedFromApaPilot) {
        this.openedFromApaPilot.set(true);
      } else {
        this.openedFromApaPilot.set(false);
      }
    });
  }

  toggleTheme(): void {
    if (this._theme.getValue() === 'light') {
      this.setTheme('dark');
    } else {
      this.setTheme('light');
    }
  }

  getTheme(): 'light' | 'dark' {
    return this._theme.getValue();
  }

  setTheme(newTheme: 'light' | 'dark'): void {
    this._theme.next(newTheme);
    localStorage.setItem(this.localStorageKeys.theme, JSON.stringify(newTheme));

    const element = this.document.querySelector('html');
    element?.classList.toggle('dark', newTheme === 'dark');
  }

  initializeVersionData(): void {
    this.versionService.initializeVersionData().subscribe({
      next: () => {
        this.versionService.getAboutBadgeValue();
      }
    });
  }

  initializeAppSettings(): void {
    this.appSettings.getUserSettings().subscribe({
      next: (settings) => {
        this.signalService.defaultTabItem$.set(settings.dashboardViewDefault);
      },
      error: (err) => {
        console.error("Failed to load app settings", err);
      }
    });
  }

  sameUserCheck(empNum: string | undefined): void {
    const currentMember = localStorage.getItem('member.loggedin');
    const currentCrewDetail = currentMember ? JSON.parse(currentMember) : null;

    if (empNum &&
      currentCrewDetail != null &&
      currentCrewDetail.memberDetails[0].username === empNum) {
      console.log(`%cActive EmpNumber: %c${empNum}`, "color: lightgreen", "color: white");
      this.signalService._authEmployeeNumber.next(empNum);
      this.signalService._empNumberObs.next(empNum);
    }
    else {
      this.adminService.clearAllStorage('mobile');
      this.signalService._authEmployeeNumber.next(empNum || '');
      this.signalService._empNumberObs.next(empNum || '');
    }
    // Init text service for navbar size
    this.textSizeService.initNavBarSizeWatcher();

    //Clear old cache greater than 7 days
    this.cacheService.clearOldCache();

    //Init version data
    this.initializeVersionData();

    // Init app settings
    this.initializeAppSettings();
  }
}





